import React, { InputHTMLAttributes } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core'
import DownloadIcon from '@src/icons/DownloadIcon'
import { browserSupportsAdvancedUpload } from '@src/routes/Configurators/components/UploadTemplate/utils'
import { FormattedMessage } from 'react-intl'
import messages from '../../messages'

export const uploadStyles = makeStyles((theme: Theme) => ({
  box: {
    textAlign: 'center',
  },
  input: {
    '& input': {
      display: 'none',
    },
    '& label': {
      cursor: 'pointer',
    },
    paddingTop: theme.spacing(7),
  },
  inputPadding: {
    paddingTop: 0,
  },
  uploadLink: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.primary.main,
  },
  uploadIcon: {
    color: theme.palette.primary.main,
    transform: 'rotate(180deg)',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 800,
  },
  advancedUpload: {
    marginBottom: theme.spacing(2),
    borderColor: theme.palette.grey[500],
    backgroundImage:
      " url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='3' ry='3' stroke='%23949494FF' stroke-width='2' stroke-dasharray='7%2c11' stroke-dashoffset='2' stroke-linecap='square'/%3e%3c/svg%3e\")",
    borderRadius: '5px',
    height: '127px',
    width: '100%',
  },
  newDesginAdvancedUpload: {
    width: 290,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0,
  },
  newDesignUploadLink: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
  },
  dragging: {
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.grey[500],
    '& $uploadLink': {
      color: theme.palette.grey[500],
    },
    border: `2px solid ${theme.palette.primary.main}`,
  },
  fieldLabel: {
    color: theme.palette.common.black,
    fontSize: theme.typography.pxToRem(14),
  },
}))

type HTMLInputElementProps = React.DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>

const UploadFile = (props) => {
  const { isConfiguratorsRedesign } = props
  const classes = uploadStyles()
  const [isDragging, setIsDragging] = React.useState<boolean>(false)
  const advancedUpload = browserSupportsAdvancedUpload()

  const onDragStart: HTMLInputElementProps['onDragStart'] = (e) => {
    e.preventDefault()
    e.stopPropagation()

    const files: DataTransferItem[] = []
    for (const item of e.dataTransfer.items) {
      if (
        item.kind === 'file' &&
        (!(props.accept && item.type) ||
          props.accept
            .split(',')
            .map((type) => type.trim().toLowerCase())
            .includes(item.type))
      ) {
        files.push(item)
      }
    }

    if (files.length > 0 && files.length === 1) {
      setIsDragging(true)
    }
  }

  const onDragStop: HTMLInputElementProps['onDragEnd'] = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const onDrop: HTMLInputElementProps['onDrop'] = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (e.dataTransfer.files) {
      const files: File[] = []
      for (const file of e.dataTransfer.files) {
        files.push(file)
      }
      props.handleUploadChange(files)
    }
  }

  const handleClick = (e) => {
    e.target.value = ''
  }

  return (
    <div
      className={clsx({
        [classes.box]: true,
        [classes.advancedUpload]: advancedUpload,
        [classes.newDesginAdvancedUpload]: isConfiguratorsRedesign,
        [classes.dragging]: isDragging,
      })}
      onDragOver={onDragStart}
      onDragLeave={onDragStop}
      onDrop={onDrop}
    >
      <div
        className={clsx(classes.input, {
          [classes.inputPadding]: isConfiguratorsRedesign,
        })}
      >
        <input
          type="file"
          name="file"
          id="file"
          accept={props.accept}
          onChange={(e) => {
            if (e.target.files) {
              props.handleUploadChange([...e.target.files])
            }
          }}
          onClick={handleClick}
        />

        <label htmlFor="file">
          <div className={classes.uploadIcon}>
            <DownloadIcon />
          </div>
          <div className={classes.fieldLabel}>
            {isConfiguratorsRedesign ? (
              <FormattedMessage {...messages.CONF_DROP_CSV_OR_XLS_FILE_OR} />
            ) : (
              <FormattedMessage {...messages.CONF_DROP_XLS_OR_XLSX_FILE_OR} />
            )}
          </div>
          <span className={classes.fieldLabel}>
            <FormattedMessage {...messages.OR} />
            &nbsp;
          </span>
          {isConfiguratorsRedesign ? (
            <span className={classes.newDesignUploadLink}>
              <FormattedMessage {...messages.BROWSE_FILES} />
            </span>
          ) : (
            <span className={classes.uploadLink}>
              <FormattedMessage {...messages.BROWSE} />
            </span>
          )}
        </label>
      </div>
    </div>
  )
}

export default UploadFile
